import React, { useState, useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Send, Mic, MicOff, Loader2, ChevronDown, ChevronUp, Search, Building2, User, Lightbulb, BarChart3, FileText, Check, Heart, Handshake, Compass, ShieldAlert, CalendarCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { trainingApi, accountApi } from "../lib/api";
import { useSpeechToText } from "../hooks/useSpeechToText";
import type { AIAgent, FormFieldSchema, CoachSection } from "../types";
import { useTranslation } from "react-i18next";
import { TrainOnMeetingDialog } from "../components/TrainOnMeetingDialog";
import { Sparkles } from "lucide-react";
import { pickPersonaField } from "../lib/persona-i18n";
import { pickFrEn } from "../lib/texte-i18n";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickDecisionMaker } from "../components/PersonaUtils";

// Mini-types pour les options de select dynamiques (chargées depuis la BDD tenant).
type OfferOption = { id: string; name: string; description?: string };
type PersonaOption = { id: string; function?: string; decisionMakerProfile?: string; painPoints?: string };

type CoachState = "init" | "form" | "processing" | "results" | "done";

interface ChatMessage {
  role: "user" | "coach";
  content: string;
}

// Payload émis par CoachPage → App.tsx quand l'user clique "Démarrer l'entraînement"
// depuis la modale TrainOnMeetingDialog. Contient tout ce qu'il faut pour reproduire
// le pattern Sales Coach Réel (localStorage bizcoach_pending_coaching_context).
export type StartTrainingPayload = {
  targetId: string;
  scenarioInstruction: string;
  suggestedDifficulty: "easy" | "medium" | "hard";
  offerId: string | null;
  personaId: string | null;
};

interface CoachPageProps {
  agent: AIAgent;
  onBack: () => void;
  onComplete: () => void;
  // Couture Prep Meeting → Training (2026-05-16) — passé par App.tsx
  // pour permettre le bouton "S'entraîner sur ce RDV" dans la fiche résultats.
  // Si non fourni, le bouton n'apparaît pas.
  userProfileType?: string;
  onStartTraining?: (payload: StartTrainingPayload, simulator: AIAgent) => void;
  // Reprise d'une session existante (brouillon ou fiche finalisée) depuis l'historique.
  // Si fourni, on charge le détail au lieu de créer une nouvelle session.
  existingSessionId?: string | null;
  // Embarqué dans un sous-onglet (page Terrain) : en-tête « Préparer mon RDV »
  // avec l'icône de la home au lieu de l'avatar/nom d'agent + pas de bouton Retour.
  embedded?: boolean;
}

export function CoachPage({ agent, onBack, onComplete, userProfileType, onStartTraining, existingSessionId, embedded }: CoachPageProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [state, setState] = useState<CoachState>("init");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [sections, setSections] = useState<CoachSection[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaReached, setQuotaReached] = useState<{ used: number; max: number } | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
  const [processingStep, setProcessingStep] = useState(0);
  // Refonte 2026-05-18 — progression progressive : status remonté par le backend
  // (researching → research_done → in_progress → completed) pour afficher l'état réel
  const [progressStatus, setProgressStatus] = useState<"researching" | "research_done" | "in_progress" | "completed" | null>(null);
  // Données dynamiques tenant pour les select_from_offers / select_from_personae
  const [tenantOffers, setTenantOffers] = useState<OfferOption[]>([]);
  const [tenantPersonae, setTenantPersonae] = useState<PersonaOption[]>([]);
  const [tenantDataLoading, setTenantDataLoading] = useState(false);
  // Recommandation simulateur (Prep Meeting V4) — code canonique + rationale
  const [recommendedSimulator, setRecommendedSimulator] = useState<string | null>(null);
  const [simulatorRationale, setSimulatorRationale] = useState<string | null>(null);
  const [showTrainDialog, setShowTrainDialog] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const { isListening, transcript, startListening, stopListening, isSupported } = useSpeechToText();

  // Box "Comment ça marche" — 1ère visite uniquement.
  // Persiste via localStorage (par device). L'user peut le fermer via ✕.
  // Pattern identique à SalesCoachNewPage pour la cohérence des 2 coachs.
  const [showOnboarding, setShowOnboarding] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("coachPrepaOnboarded") !== "true";
  });
  function dismissOnboarding() {
    try { localStorage.setItem("coachPrepaOnboarded", "true"); } catch { /* quota / safari private mode */ }
    setShowOnboarding(false);
  }

  const formSchema: FormFieldSchema[] = agent.formSchema || [];

  // Chargement des offres + personae du tenant pour les select dynamiques.
  // Lancé uniquement si le formSchema contient au moins un select_from_offers / _personae.
  useEffect(() => {
    const needsOffers = formSchema.some((f) => f.type === "select_from_offers");
    const needsPersonae = formSchema.some((f) => f.type === "select_from_personae");
    if (!needsOffers && !needsPersonae) return;

    let cancelled = false;
    setTenantDataLoading(true);
    Promise.all([
      needsOffers ? accountApi.getOffers().catch(() => []) : Promise.resolve([]),
      needsPersonae ? accountApi.getAccountPersonae().catch(() => []) : Promise.resolve([]),
    ]).then(([offers, personae]) => {
      if (cancelled) return;
      setTenantOffers((offers as OfferOption[]) || []);
      setTenantPersonae((personae as PersonaOption[]) || []);
    }).finally(() => {
      if (!cancelled) setTenantDataLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Init: soit reprise d'une session existante (brouillon / finalisée), soit nouvelle session
  useEffect(() => {
    if (state !== "init") return;
    let cancelled = false;

    if (existingSessionId) {
      // Reprise depuis l'historique : charge le détail (form data + fiche si finalisée)
      trainingApi.getCoachSessionDetail(existingSessionId)
        .then((detail) => {
          if (cancelled) return;
          setSessionId(detail.sessionId);
          if (detail.formData && Object.keys(detail.formData).length > 0) {
            setFormData(detail.formData as Record<string, unknown>);
          }
          if (detail.status === "completed" && detail.sections && detail.sections.length > 0) {
            // Fiche déjà générée → afficher directement le résultat
            setSections(detail.sections);
            setRecommendedSimulator(detail.recommendedSimulator || null);
            setSimulatorRationale(detail.simulatorRationale || null);
            setState("results");
          } else {
            // Brouillon → ouvrir le formulaire pré-rempli pour reprise
            setState("form");
          }
        })
        .catch((err: any) => {
          if (cancelled) return;
          setError(err.message || "Impossible de charger cette préparation.");
        });
      return () => { cancelled = true; };
    }

    // Nouvelle session
    trainingApi
      .createSession(agent.id)
      .then((session) => {
        if (cancelled) return;
        setSessionId(session.id);
        setState("form");
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.code === "SIMULATION_QUOTA_REACHED") {
          setQuotaReached({ used: err.used, max: err.max });
          return;
        }
        setError(err.message);
      });

    return () => { cancelled = true; };
  }, [state, agent.id]);

  // Animate processing steps (staggered timings for natural feel)
  useEffect(() => {
    if (state !== "processing") {
      setProcessingStep(0);
      return;
    }
    // Step durations in ms — accelerating pattern (slow start, faster middle, slow finish)
    const stepDelays = [0, 3000, 8000, 15000, 25000, 40000, 55000];
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < stepDelays.length; i++) {
      timers.push(setTimeout(() => setProcessingStep(i), stepDelays[i]!));
    }
    return () => timers.forEach(clearTimeout);
  }, [state]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Inject speech transcript into chat input
  const [pendingVoiceSend, setPendingVoiceSend] = useState(false);
  useEffect(() => {
    if (transcript) setChatInput(transcript);
  }, [transcript]);

  // Auto-send when mic stops and we have a transcript
  useEffect(() => {
    if (pendingVoiceSend && !isListening && chatInput.trim()) {
      setPendingVoiceSend(false);
      handleSendChat();
    }
  }, [pendingVoiceSend, isListening, chatInput]);

  const handleFieldChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Auto-save brouillon (debounced 2 sec) — Prep Meeting uniquement (simulator coach).
  // Sauvegarde le formData partiel en BDD pour que l'user puisse reprendre depuis l'historique.
  useEffect(() => {
    if (state !== "form" || !sessionId) return;
    if (Object.keys(formData).length === 0) return;
    const timer = setTimeout(() => {
      trainingApi.savePrepMeetingDraft(sessionId, formData).catch((err) => {
        // Silencieux : un échec d'auto-save ne doit pas perturber l'user
        console.warn("[coach] auto-save brouillon échoué:", err);
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [formData, sessionId, state]);

  // Polling fallback (2026-05-18) : si la requête HTTP est coupée par le proxy
  // Scalingo (timeout ~30 sec) alors que le pipeline backend tourne encore (60-90 sec),
  // on tente de récupérer la fiche en BDD via getCoachSessionDetail. Loop jusqu'à
  // status="completed" ou timeout maximum (3 min).
  const pollForCoachResult = async (sid: string): Promise<boolean> => {
    const MAX_ATTEMPTS = 60;       // 60 × 3 sec = 3 min max
    const INTERVAL_MS = 3000;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      try {
        const detail = await trainingApi.getCoachSessionDetail(sid);
        if (detail.status === "completed" && detail.sections && detail.sections.length > 0) {
          setSections(detail.sections);
          setRecommendedSimulator(detail.recommendedSimulator || null);
          setSimulatorRationale(detail.simulatorRationale || null);
          setState("results");
          return true;
        }
      } catch {
        // ignore, retry next tick
      }
    }
    return false;
  };

  // Détecte les erreurs typiques d'un timeout proxy (HTML reçu au lieu de JSON,
  // 502/504, abort fetch) qui indiquent qu'on a perdu la connexion mais que
  // le backend continue probablement.
  function isConnectionTimeoutError(err: any): boolean {
    const msg = err?.message || String(err);
    return (
      msg.includes("Unexpected token '<'") ||      // HTML page proxy reçu
      msg.includes("<!DOCTYPE") ||
      msg.includes("Failed to fetch") ||
      msg.includes("NetworkError") ||
      msg.includes("aborted") ||
      msg.includes("504") || msg.includes("502")
    );
  }

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId) return;

    // Validate required fields
    for (const field of formSchema) {
      if (field.required && !formData[field.key]) {
        setError(t("coach.requiredField", { field: pickFrEn(field.labelFr, field.labelEn, lang) }));
        return;
      }
    }

    setError(null);
    setSections([]);
    setProgressStatus(null);
    setState("processing");

    const sidForPolling = sessionId;
    console.log(`[coach] handleSubmitForm — sessionId=${sidForPolling}`);

    // Fire-and-forget du POST (le proxy Scalingo coupera à ~30 sec, mais le
    // backend continue son travail jusqu'à la fin). On n'attend PAS la réponse
    // du POST pour éviter le bruit '504 Gateway Timeout' dans la console.
    // Le polling ci-dessous fait toute la job de récupération du résultat.
    trainingApi.coachRequest(sidForPolling, formData)
      .then((result) => {
        console.log(`[coach] POST coach-request finished cleanly (rare cas pas timeout proxy)`);
        // Si le POST réussit (cas où le pipeline est <30 sec), on bascule directement
        // Mais on continue le polling au cas où il ait déjà fait le boulot
        setSections(result.sections);
        setRecommendedSimulator(result.recommendedSimulator || null);
        setSimulatorRationale(result.simulatorRationale || null);
        setState("results");
      })
      .catch((err) => {
        // Cas normal : proxy Scalingo coupe à 30 sec → on ignore, le polling prend le relais
        console.log(`[coach] POST coach-request error (attendu — proxy timeout):`, (err as Error)?.message);
      });

    // Polling pur : c'est lui qui fait tout le travail
    const MAX_POLLS = 150;  // 5 min max
    console.log(`[coach][polling] start`);
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const detail = await trainingApi.getCoachSessionDetail(sidForPolling);
        console.log(`[coach][polling] tick=${i} status=${detail.status} sections=${detail.sections?.length ?? 0}`);

        if (detail.status === "researching" || detail.status === "research_done" ||
            detail.status === "in_progress" || detail.status === "completed") {
          setProgressStatus(detail.status);
        }
        if (detail.sections && detail.sections.length > 0) {
          setSections(detail.sections);
        }
        if (detail.recommendedSimulator) setRecommendedSimulator(detail.recommendedSimulator);
        if (detail.simulatorRationale) setSimulatorRationale(detail.simulatorRationale);

        if (detail.status === "completed" && detail.sections && detail.sections.length > 0) {
          console.log(`[coach][polling] COMPLETED — bascule sur results`);
          setState("results");
          return;   // sort de handleSubmitForm
        }
      } catch (err) {
        console.warn(`[coach][polling] tick=${i} error:`, (err as Error)?.message);
      }
    }

    // Si 5 min écoulées sans complétion
    console.warn(`[coach][polling] timeout 5 min — abandonne`);
    setError("La génération prend plus longtemps que prévu. Consulte l'historique dans quelques minutes (la fiche y arrivera automatiquement).");
    setState("form");
  };

  /**
   * Relance la génération de la fiche en réutilisant les inputs courants (formData).
   * Crée une NOUVELLE session pour préserver l'historique (l'ancienne reste consultable
   * dans Historique → Préparations RDV). Utile quand on veut bénéficier d'une amélioration
   * du prompt IA sans avoir à re-saisir tout le formulaire.
   */
  const handleRegenerate = async () => {
    if (Object.keys(formData).length === 0) {
      setError("Impossible de relancer : aucune donnée de formulaire en mémoire.");
      return;
    }
    setError(null);
    setSections([]);
    setRecommendedSimulator(null);
    setSimulatorRationale(null);
    setState("processing");

    try {
      // Crée une nouvelle session pour ne pas écraser l'historique
      const newSession = await trainingApi.createSession(agent.id);
      setSessionId(newSession.id);

      const result = await trainingApi.coachRequest(newSession.id, formData);
      setSections(result.sections);
      setRecommendedSimulator(result.recommendedSimulator || null);
      setSimulatorRationale(result.simulatorRationale || null);
      setState("results");
    } catch (err: any) {
      // Réutilise la mécanique de polling fallback en cas de timeout proxy
      if (isConnectionTimeoutError(err) && sessionId) {
        const ok = await pollForCoachResult(sessionId);
        if (!ok) {
          setError("La génération prend plus longtemps que prévu. Réessaie ou consulte l'historique dans quelques minutes.");
          setState("results");   // remet sur l'ancienne fiche pour ne pas perdre l'user
        }
      } else {
        setError(err.message);
        setState("results");
      }
    }
  };

  const handleSendChat = async () => {
    if (!sessionId || !chatInput.trim() || isSending) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsSending(true);

    try {
      const response = await trainingApi.coachChat(sessionId, userMessage);
      setChatMessages((prev) => [...prev, { role: "coach", content: response }]);
    } catch (err: any) {
      setChatMessages((prev) => [...prev, { role: "coach", content: `⚠️ ${err.message}` }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  const toggleMic = () => {
    if (isListening) {
      setPendingVoiceSend(true);
      stopListening();
    } else {
      startListening(lang === "fr" ? "fr-FR" : "en-US");
    }
  };

  const handleEndSession = () => {
    if (sessionId) {
      trainingApi.updateSession(sessionId, {
        endTime: new Date().toISOString(),
        numberOfMessages: chatMessages.length + (sections.length > 0 ? 2 : 0),
      }).catch(() => {});
    }
    onComplete();
  };

  const toggleSection = (index: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const agentColor = agent.color || "#0EA5E9";

  // --- Render field based on type ---
  const renderField = (field: FormFieldSchema) => {
    // Handle conditional visibility
    // - conditionalOn seul : visible si la clé a une valeur truthy
    // - conditionalOn + conditionalOnValue : visible UNIQUEMENT si la clé == cette valeur
    //   (utilisé pour les champs "Autre — précisez" qui s'ouvrent quand select === "other")
    if (field.conditionalOn) {
      const referenced = formData[field.conditionalOn];
      if (field.conditionalOnValue !== undefined) {
        if (referenced !== field.conditionalOnValue) return null;
      } else if (!referenced) {
        return null;
      }
    }

    const label = pickFrEn(field.labelFr, field.labelEn, lang);

    switch (field.type) {
      case "text":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <Input
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
            />
          </div>
        );

      case "textarea":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <Textarea
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
            />
          </div>
        );

      case "select":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
            >
              <option value="">{t("common.choose")}</option>
              {field.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {pickFrEn(opt.labelFr, opt.labelEn, lang) || opt.label || opt.value}
                </option>
              ))}
            </select>
          </div>
        );

      case "checkbox":
        return (
          <div key={field.key} className="flex items-center gap-2">
            <input
              type="checkbox"
              id={field.key}
              checked={!!formData[field.key]}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor={field.key} className="text-sm font-medium cursor-pointer" style={{ color: "#384872" }}>
              {label}
            </Label>
          </div>
        );

      case "select_from_offers":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              disabled={tenantDataLoading}
            >
              <option value="">{tenantDataLoading ? "Chargement…" : t("common.choose")}</option>
              {tenantOffers.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
              <option value="other">Autre — précisez ci-dessous</option>
            </select>
            {tenantOffers.length === 0 && !tenantDataLoading && (
              <p className="mt-1 text-xs" style={{ color: "#9CA3AF" }}>
                Aucune offre enregistrée dans votre compte. Choisissez "Autre" pour saisir librement.
              </p>
            )}
          </div>
        );

      case "select_from_personae":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              disabled={tenantDataLoading}
            >
              <option value="">{tenantDataLoading ? t("common.loading") : t("common.choose")}</option>
              {tenantPersonae.map((p) => {
                const fn = localizePersonaOption(pickPersonaField(p, "function", i18n.language), i18n.language) || "Persona";
                const dmpLabel = pickDecisionMaker(p);
                const dmp = dmpLabel ? ` — ${dmpLabel}` : "";
                return <option key={p.id} value={p.id}>{fn}{dmp}</option>;
              })}
              <option value="other">{t("coach.otherSpecify")}</option>
            </select>
            {tenantPersonae.length === 0 && !tenantDataLoading && (
              <p className="mt-1 text-xs" style={{ color: "#9CA3AF" }}>
                Aucun persona enregistré dans votre compte. Choisissez "Autre" pour saisir librement.
              </p>
            )}
          </div>
        );

      case "date":
        return (
          <div key={field.key}>
            <Label className="mb-1.5 block text-sm font-medium" style={{ color: "#384872" }}>
              {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <Input
              type="date"
              value={(formData[field.key] as string) || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // --- Simple markdown renderer ---
  const renderMarkdown = (md: string) => {
    const html = md
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-3 mb-1" style="color:#384872">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2" style="color:#384872">$1</h2>')
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^ {2,}- (.+)$/gm, '<li class="ml-8 list-disc text-sm" style="color:#6B7A99">$1</li>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-sm" style="color:#6B7A99">$1</li>')
      .replace(/\n{2,}/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
    return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <>
    <Dialog open={!!quotaReached} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>{t("training.simulationQuotaReachedTitle")}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {quotaReached && t("training.simulationQuotaReachedMessage", { used: quotaReached.used, max: quotaReached.max })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onBack}>
            {t("training.simulationQuotaReachedBack")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <div className="space-y-3 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        {embedded ? (
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "#E7F0FA" }}><CalendarCheck className="h-5 w-5" style={{ color: "#2C6C9E" }} /></span>
            <h1 className="text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("dashboard.prepCardTitle")}</h1>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("common.back")}
            </Button>
            <div className="flex items-center gap-2">
              {agent.image ? (
                <img src={agent.image} alt={agent.name} loading="lazy" className="h-8 w-8 rounded-full object-cover" style={{ objectPosition: 'top' }} />
              ) : (
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-base"
                  style={{ backgroundColor: agentColor, color: "white" }}
                >
                  {agent.icon}
                </div>
              )}
              <h1 className="text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                {agent.name}
              </h1>
            </div>
          </div>
        )}
        {(state === "results" || state === "done") && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRegenerate} disabled={state !== "results"} title="Crée une nouvelle session avec les mêmes inputs et régénère la fiche (utile pour exploiter une amélioration du prompt IA)">
              🔄 Relancer l'analyse
            </Button>
            <Button variant="outline" size="sm" onClick={handleEndSession}>
              {t("coach.endSession")}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>
      )}

      {/* Onboarding "Comment ça marche" — 1ère visite uniquement.
          Persiste via localStorage (par device). L'user peut le fermer via ✕.
          Pattern identique à SalesCoachNewPage pour la cohérence des 2 coachs. */}
      {showOnboarding && state === "form" && (
        <div className="rounded-2xl p-4 flex items-start gap-3 relative" style={{ background: "linear-gradient(135deg, #FFF1EF 0%, #FCE3E0 100%)" }}>
          <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#F49390" }}>
            <Lightbulb className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 pr-6">
            <p className="font-semibold text-sm" style={{ color: "#C0423F" }}>
              Comment Coach Prepa vous aide
            </p>
            <p className="text-xs mt-1" style={{ color: "#4B5573" }}>
              Renseignez le contexte de votre prochain RDV ci-dessous : entreprise, interlocuteur, votre offre,
              objectif. L'IA produit en 2-3 min une fiche structurée (recherche entreprise, profil contact,
              questions à poser, points de vigilance) et reste disponible en chat pour creuser. Vous pourrez
              ensuite lancer un entraînement ciblé en un clic.
            </p>
          </div>
          <button
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 h-7 w-7 rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
            aria-label="Compris, ne plus afficher"
            title="Compris"
          >
            <span className="text-lg leading-none" style={{ color: "#C0423F" }}>×</span>
          </button>
        </div>
      )}

      {/* Init loading */}
      {state === "init" && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: agentColor }} />
          <span className="ml-3 text-sm" style={{ color: "#6B7A99" }}>{t("common.loading")}</span>
        </div>
      )}

      {/* Form */}
      {state === "form" && (
        <Card data-tour="coach-form">
          <CardContent className="p-4 md:p-6">
            <h2 className="text-base font-semibold mb-4" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("coach.fillForm")}
            </h2>
            <form onSubmit={handleSubmitForm} className="space-y-4">
              {formSchema.map(renderField)}
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  className="px-6 font-semibold"
                  style={{ backgroundColor: "#F99002" }}
                >
                  {t("coach.submit")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Affichage progressif des sections pendant le processing
          (refonte 2026-05-18 — feedback Fabien : "découpe et fournis progressivement").
          Au fur et à mesure que le polling backend remonte des sections, on les
          affiche déjà avec une animation fade-in, plutôt que d'attendre 60-90 sec. */}
      {state === "processing" && sections.length > 0 && (
        <div className="space-y-4">
          <div className="text-xs text-center mb-2 font-medium" style={{ color: "#6B7280" }}>
            ✨ Sections en cours d'arrivée…
          </div>
          {sections.map((section, index) => (
            <Card key={index} className="border-0 shadow-sm overflow-hidden animate-fade-in">
              <CardContent className="p-0">
                <div className="w-full flex items-center gap-3 px-5 py-4 bg-slate-50">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#877BF2" }}>
                    <FileText className="w-[18px] h-[18px] text-white" strokeWidth={1.8} />
                  </div>
                  <h3 className="flex-1 text-left text-[15px] font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                    {section.title}
                  </h3>
                </div>
                <div className="px-5 pb-5 pt-3 border-l-4" style={{ borderColor: "#877BF2" }}>
                  {renderMarkdown(section.content)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Indicateur de statut backend pendant le processing */}
      {state === "processing" && progressStatus && progressStatus !== "completed" && (
        <div className="rounded-xl p-3 flex items-center gap-3 text-sm" style={{ background: "#F3F0FF", color: "#5A4BBE" }}>
          <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
          <div>
            {progressStatus === "researching" && "🔍 Recherche web en cours sur 15-25 sources…"}
            {progressStatus === "research_done" && "📊 Veille terminée — synthèse commerciale en cours…"}
            {progressStatus === "in_progress" && "⚙️ Assemblage de la fiche…"}
          </div>
        </div>
      )}

      {/* Processing — Apple-style (loader principal, masqué si sections déjà affichées) */}
      {state === "processing" && sections.length === 0 && (() => {
        const stepKeys = ["processingStep1", "processingStep2", "processingStep3", "processingStep4", "processingStep5", "processingStep6"];
        const stepIcons = [Search, Building2, User, Lightbulb, BarChart3, FileText];
        const stepColors = ["#877BF2", "#F49390", "#7CBF94", "#A6D3F9", "#E6A933", "#384872"];
        const currentStep = Math.min(processingStep, 5);
        const allDone = processingStep >= 6;
        const progress = allDone ? 1 : (currentStep + 0.5) / 6;
        const color = stepColors[currentStep]!;

        const radius = 52;
        const circumference = 2 * Math.PI * radius;
        const dashoffset = circumference * (1 - progress);
        const StepIcon = stepIcons[currentStep]!;

        return (
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="py-16 md:py-20 px-8 relative">
              <style>{`
                @keyframes cp-icon {
                  0% { opacity: 0; transform: scale(0.5); }
                  100% { opacity: 1; transform: scale(1); }
                }
                @keyframes cp-text {
                  0% { opacity: 0; transform: translateY(10px); }
                  100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes cp-glow {
                  0%, 100% { opacity: 0.07; transform: scale(1); }
                  50% { opacity: 0.03; transform: scale(1.12); }
                }
                @keyframes cp-check {
                  0% { stroke-dashoffset: 24; opacity: 0; }
                  40% { opacity: 1; }
                  100% { stroke-dashoffset: 0; opacity: 1; }
                }
                .cp-icon-in { animation: cp-icon 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
                .cp-text-in { animation: cp-text 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both; }
              `}</style>

              {/* Ambient glow */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  key={`glow-${currentStep}`}
                  className="w-80 h-80 rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
                    animation: "cp-glow 4s ease-in-out infinite",
                  }}
                />
              </div>

              <div className="relative flex flex-col items-center">
                {/* Progress ring */}
                <div className="relative w-36 h-36 md:w-40 md:h-40 mb-10">
                  <svg className="w-full h-full" viewBox="0 0 120 120">
                    <defs>
                      <linearGradient id="coach-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={color} />
                        <stop offset="100%" stopColor={color} stopOpacity="0.35" />
                      </linearGradient>
                    </defs>
                    {/* Track */}
                    <circle cx="60" cy="60" r={radius} fill="none" stroke="#F1F5F9" strokeWidth="7" />
                    {/* Arc */}
                    <circle
                      cx="60" cy="60" r={radius}
                      fill="none"
                      stroke="url(#coach-ring-grad)"
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={dashoffset}
                      style={{
                        transform: "rotate(-90deg)",
                        transformOrigin: "50% 50%",
                        transition: "stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1)",
                      }}
                    />
                  </svg>

                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div key={currentStep} className="cp-icon-in">
                      {allDone ? (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7CBF94" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" style={{ strokeDasharray: 24, animation: "cp-check 0.6s ease-out 0.2s both" }} />
                        </svg>
                      ) : (
                        <StepIcon className="w-8 h-8" style={{ color }} strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Step text */}
                <div key={`t-${currentStep}`} className="cp-text-in text-center mb-8">
                  <p className="text-lg md:text-xl font-semibold tracking-tight" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                    {allDone ? t("coach.processingAlmostDone") : t(`coach.${stepKeys[currentStep]}`)}
                  </p>
                </div>

                {/* Pill-style dot indicators */}
                <div className="flex items-center gap-1.5 mb-6">
                  {stepKeys.map((_, i) => {
                    const dotActive = i === currentStep && !allDone;
                    const dotDone = i < currentStep || allDone;
                    return (
                      <div
                        key={i}
                        className="rounded-full transition-all duration-700 ease-out"
                        style={{
                          width: dotActive ? 22 : 6,
                          height: 6,
                          backgroundColor: dotDone || dotActive ? stepColors[i] : "#E2E8F0",
                          opacity: dotDone ? 0.55 : dotActive ? 1 : 0.2,
                        }}
                      />
                    );
                  })}
                </div>

                {/* Subtle hint */}
                <p className="text-xs tracking-wide" style={{ color: "#94A3B8" }}>
                  {t("coach.processingHint")}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Results + Chat */}
      {(state === "results" || state === "done") && (() => {
        // Section styling — map by index with fallback
        const sectionStyles = [
          { icon: Building2, color: "#877BF2", bg: "#EAEAFE" },
          { icon: Heart, color: "#F49390", bg: "#FFEFEE" },
          { icon: Handshake, color: "#7CBF94", bg: "#eef6f1" },
          { icon: Compass, color: "#E6A933", bg: "#FFF8E7" },
        ];
        const fallbackStyle = { icon: FileText, color: "#384872", bg: "#F1F5F9" };

        return (
        <>
          {/* CTA "S'entraîner sur ce RDV" en TOP de fiche (Prep Meeting uniquement,
              identifié par la présence de recommendedSimulator ou simulatorRationale) */}
          {(recommendedSimulator || simulatorRationale) && onStartTraining && (
            <TrainOnMeetingCTA onClick={() => setShowTrainDialog(true)} position="top" />
          )}

          {/* Result sections */}
          <div className="space-y-4">
            {sections.map((section, index) => {
              const style = sectionStyles[index] || fallbackStyle;
              const Icon = style.icon;
              const isCollapsed = collapsedSections.has(index);

              return (
                <Card key={index} className="border-0 shadow-sm overflow-hidden">
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-5 py-4 transition-colors hover:opacity-90"
                      style={{ backgroundColor: style.bg }}
                      onClick={() => toggleSection(index)}
                    >
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: style.color }}
                      >
                        <Icon className="w-[18px] h-[18px] text-white" strokeWidth={1.8} />
                      </div>
                      <h3 className="flex-1 text-left text-[15px] font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                        {section.title}
                      </h3>
                      <div
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-300"
                        style={{ backgroundColor: `${style.color}18`, transform: isCollapsed ? "rotate(0deg)" : "rotate(180deg)" }}
                      >
                        <ChevronDown className="w-4 h-4" style={{ color: style.color }} />
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="px-5 pb-5 pt-3" style={{ borderLeft: `3px solid ${style.color}`, marginLeft: "20px" }}>
                        {renderMarkdown(section.content)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA "S'entraîner sur ce RDV" en BOTTOM de fiche */}
          {(recommendedSimulator || simulatorRationale) && onStartTraining && (
            <TrainOnMeetingCTA onClick={() => setShowTrainDialog(true)} position="bottom" />
          )}

          {/* Chat Q&A */}
          {state === "results" && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-base font-semibold mb-3" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  {t("coach.askQuestion")}
                </h3>

                {/* Chat messages zone */}
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-4 min-h-[120px] max-h-96 overflow-y-auto">
                  {chatMessages.length === 0 && !isSending && (
                    <p className="text-center text-sm py-8" style={{ color: "#94A3B8" }}>
                      {t("coach.chatEmpty")}
                    </p>
                  )}
                  <div className="space-y-3">
                    {chatMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                            msg.role === "user"
                              ? "text-white"
                              : "bg-white border border-slate-100 shadow-sm"
                          }`}
                          style={msg.role === "user" ? { backgroundColor: "#384872" } : { color: "#384872" }}
                        >
                          {msg.role === "coach" ? renderMarkdown(msg.content) : msg.content}
                        </div>
                      </div>
                    ))}
                    {isSending && (
                      <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-xl bg-white border border-slate-100 shadow-sm">
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: agentColor }} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div ref={chatEndRef} />
                </div>

                {/* Listening indicator */}
                {isListening && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-2" style={{ backgroundColor: "#FFEFEE" }}>
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                    </span>
                    <span className="text-sm font-medium" style={{ color: "#F49390" }}>{t("coach.listening")}</span>
                  </div>
                )}

                {/* Chat input */}
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isListening ? t("coach.listening") : t("coach.chatPlaceholder")}
                    disabled={isSending || isListening}
                    className="flex-1"
                  />
                  {isSupported && (
                    <Button
                      type="button"
                      variant={isListening ? "default" : "outline"}
                      size="icon"
                      onClick={toggleMic}
                      title={t("coach.micTooltip")}
                      aria-label={t("coach.micTooltip")}
                      className={isListening ? "animate-pulse" : ""}
                      style={isListening ? { backgroundColor: "#F49390", borderColor: "#F49390" } : undefined}
                    >
                      {isListening ? (
                        <MicOff className="h-4 w-4 text-white" />
                      ) : (
                        <Mic className="h-4 w-4" style={{ color: "#384872" }} />
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={handleSendChat}
                    disabled={!chatInput.trim() || isSending || isListening}
                    style={{ backgroundColor: "#F99002" }}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
        );
      })()}
    </div>

    {/* Modale "S'entraîner sur ce RDV" (Prep Meeting → Training, 2026-05-16) */}
    {sessionId && userProfileType && onStartTraining && (
      <TrainOnMeetingDialog
        open={showTrainDialog}
        onOpenChange={setShowTrainDialog}
        sessionId={sessionId}
        recommendedSimulatorCode={recommendedSimulator}
        simulatorRationale={simulatorRationale}
        userProfileType={userProfileType}
        meetingContext={{
          companyName: typeof formData.companyName === "string" ? formData.companyName : undefined,
          contactName: typeof formData.contactName === "string" ? formData.contactName : undefined,
          meetingObjective: typeof formData.meetingObjective === "string" ? formData.meetingObjective : undefined,
        }}
        onTargetCreated={onStartTraining}
      />
    )}
    </>
  );
}

// Sous-composant : CTA "S'entraîner sur ce RDV" affiché en haut + bas de la fiche.
function TrainOnMeetingCTA({ onClick, position }: { onClick: () => void; position: "top" | "bottom" }) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl p-4 md:p-5 flex items-center gap-3 transition-all active:scale-[0.98] shadow-sm hover:shadow-md"
      style={{ background: "linear-gradient(135deg, #988EF2 0%, #5A4BBE 100%)" }}
    >
      <div className="h-11 w-11 md:h-12 md:w-12 rounded-full bg-white/25 flex items-center justify-center shrink-0">
        <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-white" />
      </div>
      <div className="flex-1 text-left">
        <div className="font-bold text-sm md:text-base text-white">
          🎯 S'entraîner sur ce RDV {position === "top" ? "avant la rencontre" : ""}
        </div>
        <div className="text-xs md:text-sm mt-0.5 text-white/90">
          {position === "top"
            ? "L'IA recommande le simulateur idéal pour préparer cet échange"
            : "Lance une simulation IA basée sur cette préparation"}
        </div>
      </div>
      <div className="hidden sm:block text-white/80 font-medium text-sm">→</div>
    </button>
  );
}
